import { useMemo } from 'react';

import stockService from '@services/stock';
import useStockBuySellContext from './useStockBuySellContext';

const useValidateForm = (props = {}) => {
  const { orderDetail } = props;

  const {
    code,
    formState,
    formKey,
    isAmend,
    isBuy,
    isSell,
    isAccountRegular,
    isAccountDT,
    isAccountMT,
    isLimitOrder,
    isAutoOrder,
    maxBuy,
    maxSell,
  } = useStockBuySellContext();
  const { isUntradeable: isDtUntradeable } = stockService.detail.dt.tradeable.useDtTradeable(code);
  const { isUntradeable: isMtUntradeable } = stockService.detail.mt.tradeable.useMtTradeable(code);

  const validateGeneral = () => {
    if (!formState[formKey.CALC_ORDER_TOTAL] || formState[formKey.CALC_ORDER_TOTAL] <= 0) {
      // Total has to exist, and its value cannot be negative
      return false;
    }
    return true;
  };

  const validateAmendLimit = () => {
    if (formState[formKey.INPUT_LOT_COUNT] > formState[formKey.CONST_MAX_LOT_AMEND]) {
      // Cannot increase lot count
      // (Decrease is allowed)
      return false;
    }
    if (
      formState[formKey.INPUT_PRICE] === orderDetail?.price &&
      formState[formKey.INPUT_LOT_COUNT] === orderDetail?.lot_count
    ) {
      // If form is unchanged, cannot submit order
      return false;
    }
    return true;
  };

  const validateAmendAuto = () => {
    if (
      formState[formKey.INPUT_PRICE] === orderDetail?.price &&
      formState[formKey.INPUT_LOT_COUNT] === orderDetail?.lot_count &&
      formState[formKey.INPUT_START_DATE] === orderDetail?.start_date &&
      formState[formKey.INPUT_END_DATE] === orderDetail?.end_date &&
      formState[formKey.INPUT_THRESHOLD_COMPARATOR] === orderDetail?.threshold_comparator &&
      formState[formKey.INPUT_THRESHOLD_VALUE] === orderDetail?.threshold_value
    ) {
      // If form is unchanged, cannot submit order
      return false;
    }
    return true;
  };

  const validateBuyLimit = () => {
    if (formState[formKey.CALC_ORDER_TOTAL] > maxBuy.value) {
      // Total has to be below max total limit
      return false;
    }
    return true;
  };

  const validateSellLimit = () => {
    if (formState[formKey.INPUT_LOT_COUNT] > maxSell.value) {
      // Lot count has to be below max lot count limit
      return false;
    }
    return true;
  };

  const validateBuyAuto = () => {
    if (!formState[formKey.INPUT_START_DATE] || !formState[formKey.INPUT_END_DATE]) {
      // User has to select a start & end date
      return false;
    }
    if (!formState[formKey.INPUT_THRESHOLD_COMPARATOR]) {
      // User has to select a threshold comparator
      return false;
    }
    if (
      !formState[formKey.INPUT_THRESHOLD_VALUE] ||
      formState[formKey.INPUT_THRESHOLD_VALUE] <= 0
    ) {
      // User has to input a threshold value and cannot be negative
      return false;
    }
    return true;
  };

  const validateSellAuto = () => {
    if (!formState[formKey.INPUT_START_DATE] || !formState[formKey.INPUT_END_DATE]) {
      // User has to select a start & end date
      return false;
    }
    if (!formState[formKey.INPUT_THRESHOLD_COMPARATOR]) {
      // User has to select a threshold comparator
      return false;
    }
    if (
      !formState[formKey.INPUT_THRESHOLD_VALUE] ||
      formState[formKey.INPUT_THRESHOLD_VALUE] <= 0
    ) {
      // User has to input a threshold value and cannot be negative
      return false;
    }
    if (formState[formKey.INPUT_LOT_COUNT] > maxSell.value) {
      // Lot count has to be below max lot count limit
      return false;
    }
    return true;
  };

  const validateAccountDT = () => {
    if (isDtUntradeable && isBuy) {
      return false;
    }
    return true;
  };

  const validateAccountMT = () => {
    if (isMtUntradeable) {
      return false;
    }
    return true;
  };

  const validateAll = () => {
    if (!validateGeneral()) {
      return false;
    }

    if (isAmend) {
      if (isLimitOrder && !validateAmendLimit()) {
        return false;
      }
      if (isAutoOrder && !validateAmendAuto()) {
        return false;
      }
    }

    switch (true) {
      // Regular account
      case isAccountRegular && isBuy && isLimitOrder:
        return validateBuyLimit();
      case isAccountRegular && isSell && isLimitOrder:
        return validateSellLimit();
      case isAccountRegular && isBuy && isAutoOrder:
        return validateBuyAuto();
      case isAccountRegular && isSell && isAutoOrder:
        return validateSellAuto();
      // DT account
      case isAccountDT && isBuy && isLimitOrder:
        return validateBuyLimit() && validateAccountDT();
      case isAccountDT && isSell && isLimitOrder:
        return validateSellLimit() && validateAccountDT();
      // MT account
      case isAccountMT && isBuy && isLimitOrder:
        return validateBuyLimit() && validateAccountMT();
      case isAccountMT && isSell && isLimitOrder:
        return validateSellLimit() && validateAccountMT();
      case isAccountMT && isBuy && isAutoOrder:
        return validateBuyAuto() && validateAccountMT();
      case isAccountMT && isSell && isAutoOrder:
        return validateSellAuto() && validateAccountMT();
      default:
        return true;
    }
  };

  const isFormValidated = useMemo(() => validateAll(), [formState]);

  return { isFormValidated, isBuy, isSell, isLimitOrder, isAutoOrder };
};

export default useValidateForm;
